import { useState } from 'react';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

interface CompactAlertProps {
  tone: AlertTone;
  title: string;
  description?: string;
  details?: string[];
  compact?: boolean;
  className?: string;
}

const TONE_CONFIG: Record<AlertTone, {
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  icon: string;
  titleColor: string;
  descColor: string;
  expandBg: string;
  expandText: string;
}> = {
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    icon: 'ri-information-line',
    titleColor: 'text-sky-800',
    descColor: 'text-sky-600',
    expandBg: 'bg-sky-100',
    expandText: 'text-sky-700',
  },
  success: {
    bg: 'bg-agrida-50',
    border: 'border-agrida-200',
    iconBg: 'bg-agrida-100',
    iconColor: 'text-agrida-600',
    icon: 'ri-checkbox-circle-line',
    titleColor: 'text-agrida-800',
    descColor: 'text-agrida-600',
    expandBg: 'bg-agrida-100',
    expandText: 'text-agrida-700',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    icon: 'ri-alert-line',
    titleColor: 'text-amber-800',
    descColor: 'text-amber-600',
    expandBg: 'bg-amber-100',
    expandText: 'text-amber-700',
  },
  danger: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    icon: 'ri-error-warning-line',
    titleColor: 'text-red-800',
    descColor: 'text-red-600',
    expandBg: 'bg-red-100',
    expandText: 'text-red-700',
  },
};

export function CompactAlert({
  tone,
  title,
  description,
  details,
  compact = false,
  className = '',
}: CompactAlertProps) {
  const [expanded, setExpanded] = useState(false);
  const c = TONE_CONFIG[tone];

  if (compact) {
    return (
      <div className={`${c.bg} ${c.border} border rounded-2xl p-3 flex items-start gap-2.5 overflow-hidden ${className}`}>
        <span className={`w-8 h-8 rounded-xl ${c.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
          <i className={`${c.icon} ${c.iconColor} text-sm`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[13px] font-semibold ${c.titleColor} leading-snug text-safe-normal`}>{title}</p>
          {details && details.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className={`mt-1.5 text-[11px] font-medium ${c.expandText} ${c.expandBg} px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} />
              </span>
              {expanded ? 'Thu gọn' : `${details.length} chi tiết`}
            </button>
          )}
          {expanded && details && (
            <ul className={`mt-2 space-y-1 border-t ${c.border} pt-2`}>
              {details.map((d, i) => (
                <li key={i} className={`text-[11px] ${c.descColor} flex gap-1.5`}>
                  <span className="shrink-0 mt-1 w-1 h-1 rounded-full bg-current opacity-50" />
                  <span className="leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${c.bg} ${c.border} border rounded-2xl p-4 overflow-hidden ${className}`}>
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center shrink-0`}>
          <i className={`${c.icon} ${c.iconColor} text-lg`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-bold ${c.titleColor} mb-1`}>{title}</p>
          {description && (
            <p className={`text-xs ${c.descColor} leading-relaxed`}>{description}</p>
          )}
          {details && details.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className={`mt-2 text-[11px] font-medium ${c.expandText} ${c.expandBg} px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} />
                </span>
                {expanded ? 'Thu gọn' : `Xem ${details.length} chi tiết`}
              </button>
              {expanded && (
                <ul className={`mt-2.5 space-y-1.5 border-t ${c.border} pt-2.5`}>
                  {details.map((d, i) => (
                    <li key={i} className={`text-[11px] ${c.descColor} flex gap-1.5`}>
                      <span className="shrink-0 mt-1 w-1 h-1 rounded-full bg-current opacity-50" />
                      <span className="leading-relaxed">{d}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
